import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { X, Mail, Phone, MapPin, Globe, User, FileText } from "lucide-react";

const COLORS = { "New lead": "#64748b", "Application started": "#7A2FD4", "Assessment started": "#9B5DE5", "Assessment completed": "#9B5DE5", "Under review": "#f59e0b", "Interview invited": "#f59e0b", "Admitted": "#10b981", "Conditionally admitted": "#10b981", "Offer sent": "#06b6d4", "Offer accepted": "#06b6d4", "Deposit paid": "#10b981", "Waitlisted": "#f97316", "Not admitted": "#ef4444", "Enrolled": "#10b981" };

export default function CRM() {
  const [data, setData] = useState(null);
  const [progMap, setProgMap] = useState({});
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/admin/pipeline").then((r) => setData(r.data));
  useEffect(() => {
    load();
    api.get("/programmes").then((r) => {
      const m = {}; (r.data || []).forEach((p) => { m[p.slug] = p.name_en || p.name_fr || p.slug; }); setProgMap(m);
    }).catch(() => {});
  }, []);

  const progName = (slug) => progMap[slug] || slug || "—";

  const move = async (appId, stage) => {
    try { await api.patch(`/admin/applications/${appId}/stage`, { stage }); toast.success("Stage updated"); load(); }
    catch { toast.error("Could not update stage"); }
  };

  if (!data) return <AdminLayout><div className="p-10 text-paper/40">Loading…</div></AdminLayout>;
  const activeStages = data.stages.filter((s) => (data.board[s] || []).length > 0);

  return (
    <AdminLayout>
      <div className="p-8 md:p-10">
        <div className="eyebrow text-heraldic mb-1">Admissions</div>
        <h1 className="font-serif text-4xl text-paper">CRM pipeline</h1>
        <p className="text-paper/50 text-sm mt-1">Use the menu on each card to advance a candidate — or open a card to see everything the applicant submitted.</p>

        <div className="mt-8 flex gap-4 overflow-x-auto pb-6">
          {activeStages.map((stage) => (
            <div key={stage} className="w-72 shrink-0">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[stage] || "#7A2FD4" }} />
                <span className="text-sm text-paper/80">{stage}</span>
                <span className="text-xs text-paper/40">{data.board[stage].length}</span>
              </div>
              <div className="space-y-2">
                {data.board[stage].map((a) => (
                  <div key={a.id} data-testid={`crm-card-${a.application_id}`} className="border border-white/10 rounded-sm p-3 bg-white/[0.03] hover:border-heraldic/40 transition-colors">
                    <button onClick={() => setDetail(a)} data-testid={`crm-open-${a.application_id}`} className="text-left w-full">
                      <div className="flex justify-between items-start">
                        <div className="font-medium text-paper text-sm">{a.applicant_name}</div>
                        <span className="font-mono text-[10px] text-heraldic">{a.application_id}</span>
                      </div>
                      <div className="text-xs text-paper/50 mt-1">{progName(a.programme)}</div>
                      <div className="flex items-center gap-2 mt-2 text-[11px] text-paper/40">
                        <span className="capitalize">{a.market}</span>·<span>{a.source}</span>·<span className={a.payment_status === "paid" ? "text-emerald-400" : ""}>{a.payment_status}</span>
                      </div>
                    </button>
                    <select data-testid={`crm-move-${a.application_id}`} value={stage} onChange={(e) => move(a.application_id, e.target.value)}
                      className="mt-2 w-full bg-[#14161c] border border-white/10 rounded-sm text-xs text-paper/70 px-2 py-1.5">
                      {data.stages.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {detail && <ApplicationDetail a={detail} progName={progName} onClose={() => setDetail(null)} />}
    </AdminLayout>
  );
}

function Row({ icon: Icon, label, value }) {
  if (value === undefined || value === null || value === "" ) return null;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-white/5 last:border-0">
      {Icon && <Icon className="h-3.5 w-3.5 text-paper/40 mt-0.5 shrink-0" />}
      <div className="text-[11px] uppercase tracking-wide text-paper/40 w-36 shrink-0">{label}</div>
      <div className="text-sm text-paper/85 break-words">{String(value)}</div>
    </div>
  );
}

function DecisionPanel({ a, onDone }) {
  const [type, setType] = useState(null);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [payLink, setPayLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = async (t) => {
    setBusy(true); setType(t);
    try {
      const { data } = await api.get(`/admin/applications/${a.application_id}/decision`, { params: { type: t } });
      setSubject(data.subject); setBodyHtml(data.body_html); setPayLink(data.will_create_payment_link);
    } catch (e) { toast.error("Could not build preview"); setType(null); }
    setBusy(false);
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/applications/${a.application_id}/decision`, {
        type, subject, body_html: bodyHtml, create_payment_link: true });
      toast.success(`Sent — moved to "${data.stage}"${data.payment_link ? " · payment link created" : ""}`);
      onDone && onDone();
    } catch (e) { toast.error("Could not send decision"); }
    setBusy(false);
  };
  return (
    <div className="mb-4" data-testid="crm-decision-panel">
      <div className="flex gap-2 mb-3">
        <button onClick={() => preview("admit")} disabled={busy} data-testid="decision-admit" className="text-xs px-3 py-1.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Admit</button>
        <button onClick={() => preview("request_info")} disabled={busy} data-testid="decision-request-info" className="text-xs px-3 py-1.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30">Request info</button>
        <button onClick={() => preview("reject")} disabled={busy} data-testid="decision-reject" className="text-xs px-3 py-1.5 rounded-sm bg-red-500/15 text-red-300 border border-red-500/30">Reject</button>
      </div>
      {type && (
        <div className="space-y-2 border border-white/10 rounded-sm p-3 bg-white/[0.02]">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="decision-subject" className="w-full bg-[#0B0D12] border border-white/15 rounded-sm px-2 py-1.5 text-sm text-paper" />
          <textarea value={bodyHtml.replace(/<[^>]+>/g, (m) => m)} onChange={(e) => setBodyHtml(e.target.value)} data-testid="decision-body" rows={10} className="w-full bg-[#0B0D12] border border-white/15 rounded-sm px-2 py-1.5 text-xs text-paper/80 font-mono" />
          {payLink && type === "admit" && <div className="text-[11px] text-emerald-300/80">A program-tailored payment link + bank-transfer instructions will be generated and included automatically.</div>}
          <div className="flex gap-2">
            <button onClick={send} disabled={busy} data-testid="decision-send" className="text-sm px-4 py-1.5 rounded-sm bg-heraldic text-white disabled:opacity-50">{busy ? "Sending…" : "Preview → Send"}</button>
            <button onClick={() => setType(null)} className="text-sm px-3 py-1.5 rounded-sm border border-white/15 text-paper/60">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ApplicationDetail({ a, progName, onClose }) {
  const steps = a.steps || {};
  const stepKeys = Object.keys(steps).filter((k) => steps[k] && Object.keys(steps[k]).length > 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="crm-detail-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-[#0B0D12] border border-white/10 rounded-md w-full max-w-lg max-h-[85vh] overflow-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0B0D12]">
          <div>
            <div className="font-serif text-xl text-paper">{a.applicant_name}</div>
            <div className="font-mono text-[11px] text-heraldic">{a.application_id} · <span className="text-paper/50">{a.stage}</span></div>
          </div>
          <button onClick={onClose} data-testid="crm-detail-close"><X className="h-5 w-5 text-paper/50" /></button>
        </div>
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wide text-heraldic mb-1">Applicant & contact</div>
          <div className="mb-4">
            <Row icon={FileText} label="Programme" value={progName(a.programme)} />
            <Row icon={Mail} label="Email" value={a.email} />
            <Row icon={Phone} label="Phone" value={a.phone} />
            <Row icon={Phone} label="WhatsApp" value={a.whatsapp} />
            <Row icon={MapPin} label="Country of residence" value={a.country_residence} />
            <Row icon={MapPin} label="Study location" value={a.study_location} />
            <Row icon={Globe} label="Citizenship" value={a.citizenship} />
            <Row icon={Globe} label="Language" value={a.language} />
            <Row icon={User} label="Student age" value={a.student_age} />
            <Row label="Is minor" value={a.is_minor === true ? "Yes" : a.is_minor === false ? "No" : undefined} />
          </div>

          <div className="text-[11px] uppercase tracking-wide text-heraldic mb-1">Pipeline</div>
          <div className="mb-4">
            <Row label="Source" value={a.source} />
            <Row label="Market" value={a.market} />
            <Row label="Payment status" value={a.payment_status} />
            <Row label="Consent" value={a.consent === true ? "Given" : a.consent === false ? "Not given" : undefined} />
            <Row label="Owner" value={a.owner} />
            <Row label="Next action" value={a.next_action} />
            <Row label="Created" value={(a.created_at || "").slice(0, 10)} />
          </div>

          <div className="text-[11px] uppercase tracking-wide text-heraldic mb-1">Admission decision</div>
          <DecisionPanel a={a} onDone={onClose} />

          <div className="text-[11px] uppercase tracking-wide text-heraldic mb-1">Submitted form data</div>
          {stepKeys.length === 0 ? (
            <div className="text-sm text-paper/40 py-2">No additional form steps submitted yet.</div>
          ) : (
            <div className="space-y-3" data-testid="crm-detail-steps">
              {stepKeys.map((sk) => (
                <div key={sk} className="border border-white/10 rounded-sm p-3 bg-white/[0.03]">
                  <div className="text-xs font-medium text-paper/70 capitalize mb-1">{sk.replace(/[-_]/g, " ")}</div>
                  {Object.entries(steps[sk]).map(([k, v]) => (
                    <Row key={k} label={k.replace(/[-_]/g, " ")} value={typeof v === "object" ? JSON.stringify(v) : v} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
